import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, Button, Input, Select, Empty, Badge } from "../../components/admin/UI";
import { Modal, useModal } from "../../components/admin/Modal";
import { Plus, Trash2, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { scheduled: "Programmé", played: "Joué", cancelled: "Annulé" };
const STATUS_COLOR = { scheduled: "amber", played: "emerald", cancelled: "red" };

export default function Matches() {
    const { activeClubId, activeSeasonId } = useAuth();
    const [matches, setMatches] = useState([]);
    const [teams, setTeams] = useState([]);
    const [filterTeam, setFilterTeam] = useState("");
    const { open, openModal, closeModal } = useModal();
    const empty = { date: "", time: "", location: "", competition: "", opponent: "", status: "scheduled", our_score: "", opponent_score: "", home_away: "home", team_id: "" };
    const [form, setForm] = useState(empty);

    const load = () => {
        if (!activeClubId) return;
        const params = { club_id: activeClubId };
        if (activeSeasonId) params.season_id = activeSeasonId;
        api.get("/teams", { params }).then((r) => setTeams(r.data));
        const p2 = { ...params };
        if (filterTeam) p2.team_id = filterTeam;
        api.get("/matches", { params: p2 }).then((r) => setMatches(r.data));
    };
    useEffect(load, [activeClubId, activeSeasonId, filterTeam]);

    const submit = async (e) => {
        e.preventDefault();
        const payload = { ...form, season_id: activeSeasonId };
        ["our_score", "opponent_score"].forEach((k) => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
        try {
            await api.post("/matches", payload, { params: { club_id: activeClubId } });
            toast.success("Match créé");
            closeModal();
            setForm(empty);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const updateStatus = async (id, status, score) => {
        try {
            await api.patch(`/matches/${id}`, { status, ...score }, { params: { club_id: activeClubId } });
            toast.success("Match mis à jour");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer ce match ?")) return;
        await api.delete(`/matches/${id}`, { params: { club_id: activeClubId } });
        toast.success("Supprimé");
        load();
    };

    const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";

    return (
        <div data-testid="matches-page">
            <PageHeader
                title="Matchs"
                subtitle="Calendrier, résultats et statistiques basket"
                action={<Button onClick={openModal} disabled={!teams.length} data-testid="add-match-button"><Plus className="w-4 h-4" /> Nouveau match</Button>}
            />

            <div className="flex flex-wrap gap-3 mb-4">
                <Select label="Filtrer par équipe" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} className="w-64">
                    <option value="">Toutes</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
            </div>

            {matches.length === 0 ? (
                <Empty title="Aucun match" hint="Programmez votre premier match." />
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr className="text-left">
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Date</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Équipe</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Adversaire</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Score</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Statut</th>
                                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matches.map((m) => (
                                    <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`match-row-${m.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-slate-900">{m.date}</div>
                                            <div className="text-xs text-slate-500">{m.time || ""} {m.location && `· ${m.location}`}</div>
                                        </td>
                                        <td className="px-4 py-3"><Badge color="slate">{teamName(m.team_id)}</Badge></td>
                                        <td className="px-4 py-3">
                                            <div className="font-semibold">vs {m.opponent}</div>
                                            <div className="text-xs text-slate-500">{m.competition || "—"} · {m.home_away === "home" ? "Domicile" : m.home_away === "away" ? "Extérieur" : "Neutre"}</div>
                                        </td>
                                        <td className="px-4 py-3 font-display font-bold">
                                            {m.our_score != null && m.opponent_score != null ? (
                                                <span className={m.our_score > m.opponent_score ? "text-emerald-600" : m.our_score < m.opponent_score ? "text-red-600" : ""}>
                                                    {m.our_score} - {m.opponent_score}
                                                </span>
                                            ) : <span className="text-slate-400">—</span>}
                                        </td>
                                        <td className="px-4 py-3"><Badge color={STATUS_COLOR[m.status]}>{STATUS_LABEL[m.status]}</Badge></td>
                                        <td className="px-4 py-3 text-right space-x-2">
                                            <Link to={`/admin/stats/${m.id}`} className="inline-flex items-center text-orange-500 hover:text-orange-600 text-xs font-semibold" data-testid={`stats-link-${m.id}`}><BarChart3 className="w-4 h-4 mr-1" /> Stats</Link>
                                            {m.status !== "played" && (
                                                <button onClick={() => {
                                                    const ours = window.prompt("Score équipe ?", m.our_score || 0);
                                                    const opp = window.prompt("Score adversaire ?", m.opponent_score || 0);
                                                    if (ours != null && opp != null) updateStatus(m.id, "played", { our_score: Number(ours), opponent_score: Number(opp) });
                                                }} className="text-emerald-600 hover:text-emerald-700 text-xs font-semibold">Marquer joué</button>
                                            )}
                                            <button onClick={() => remove(m.id)} className="text-slate-500 hover:text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            <Modal open={open} onClose={closeModal} title="Nouveau match" size="lg">
                <form onSubmit={submit} className="space-y-4" data-testid="match-form">
                    <div className="grid grid-cols-2 gap-3">
                        <Select label="Équipe" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} required>
                            <option value="">Sélectionner...</option>
                            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </Select>
                        <Input label="Adversaire" value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} required />
                        <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                        <Input label="Heure" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
                        <Input label="Lieu" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                        <Input label="Compétition" value={form.competition} onChange={(e) => setForm({ ...form, competition: e.target.value })} />
                        <Select label="Lieu" value={form.home_away} onChange={(e) => setForm({ ...form, home_away: e.target.value })}>
                            <option value="home">Domicile</option>
                            <option value="away">Extérieur</option>
                            <option value="neutral">Neutre</option>
                        </Select>
                        <Select label="Statut" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                            <option value="scheduled">Programmé</option>
                            <option value="played">Joué</option>
                            <option value="cancelled">Annulé</option>
                        </Select>
                        {form.status === "played" && (
                            <>
                                <Input label="Notre score" type="number" value={form.our_score} onChange={(e) => setForm({ ...form, our_score: e.target.value })} />
                                <Input label="Score adverse" type="number" value={form.opponent_score} onChange={(e) => setForm({ ...form, opponent_score: e.target.value })} />
                            </>
                        )}
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" type="button" onClick={closeModal}>Annuler</Button>
                        <Button type="submit" data-testid="match-submit">Créer</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
